/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { createEntity } from 'jotai/entity';
import { SystemWorkspaceId, systemWorkspaceIdSlug } from '../SystemWorkspaceId';
import { WorkspaceObject } from '../WorkspaceObject';
import { OriginType, RunType, SanctioningBodyEnum } from 'models/viewModels';

export const defaultQualifying: WorkspaceObject = {
  id: SystemWorkspaceId.DefaultQualifying,
  name: 'Default Qualifying',
  slug: systemWorkspaceIdSlug.defaultQualifying,
  owner: 'system',
  sessionTypes: [RunType.QUALIFYING],
  sanctioningBody: SanctioningBodyEnum.NASCAR,
  activeView: 't0',
  views: {
    byId: {
      t0: {
        id: 't0',
        name: 'Race',
        visualizations: ['v0', 'v1', 'v4', 'v5', 'v6'],
        layouts: 'vl0',
      },
      t1: {
        id: 't1',
        name: 'Weather',
        visualizations: ['v2', 'v3'],
        layouts: 'vl1',
      },
      t2: {
        id: 't2',
        name: 'Track Temp',
        visualizations: ['v9'],
        layouts: 'vl4',
      },
    },
    allIds: ['t0', 't1', 't2'],
  },
  viewLayouts: {
    byId: {
      vl0: [
        {
          w: 5,
          h: 41,
          x: 3,
          y: 10,
          i: 'v0',
          moved: false,
          static: false,
        },
        {
          w: 4,
          h: 41,
          x: 8,
          y: 10,
          i: 'v1',
          moved: false,
          static: false,
        },
        {
          w: 3,
          h: 16,
          x: 0,
          y: 10,
          i: 'v4',
          moved: false,
          static: false,
        },
        {
          w: 12,
          h: 10,
          x: 0,
          y: 0,
          i: 'v5',
          moved: false,
          static: false,
        },
        {
          w: 3,
          h: 25,
          x: 0,
          y: 26,
          i: 'v6',
          moved: false,
          static: false,
        },
      ],
      vl1: [
        {
          i: 'v2',
          x: 0,
          y: 0,
          w: 6,
          h: 20,
        },
        {
          i: 'v3',
          x: 6,
          y: 0,
          w: 6,
          h: 20,
        },
      ],
      vl4: [
        {
          i: 'v9',
          w: 12,
          h: 50,
          x: 0,
          y: 0,
        },
      ],
    },
    allIds: ['vl0', 'vl1', 'vl4'],
  },
  visualizations: {
    byId: {
      v0: {
        id: 'v0',
        name: 'Qualifying Order Table',
        type: 'raceOrderTable',
        settings: 'vs0',
      },
      v1: {
        id: 'v1',
        name: 'Track View Map',
        type: 'trackViewMap',
        settings: 'vs1',
      },
      v2: {
        id: 'v2',
        name: 'Weather',
        type: 'weather',
        settings: 'vs2',
      },
      v3: {
        id: 'v3',
        name: 'Weather Radar Map',
        type: 'weatherMap',
        settings: 'vs3',
      },
      v9: {
        id: 'v9',
        name: 'Track Temperature',
        type: 'trackTemperature',
        settings: 'vs9',
      },
      v4: {
        id: 'v4',
        name: 'Global Competitor Groups',
        type: 'competitorGroupsDock',
        settings: 'vs4',
      },
      v5: {
        id: 'v5',
        name: 'Lap Times Table',
        type: 'lapTimesTable',
        settings: 'vs5',
      },
      v6: {
        id: 'v6',
        name: 'Forecast',
        type: 'weather',
        settings: 'vs6',
      },
    },
    allIds: ['v0', 'v1', 'v2', 'v3', 'v9', 'v4', 'v5', 'v6'],
  },
  visualizationSettings: {
    byId: {
      vs0: {
        id: 'vs0',
        columnStates: [
          {
            colId: 'runningPosition',
            sort: 'asc',
            flex: 1,
          },
          {
            colId: 'qualifyingPosition',
            sort: 'asc',
            flex: 1,
          },
          {
            colId: 'vehicleNumber',
            flex: 1,
          },
          {
            colId: 'driverFullName',
            hide: true,
            flex: 1,
          },
          {
            colId: 'lapsCompleted',
            hide: true,
            flex: 1,
          },
          {
            colId: 'deltaToTarget',
            flex: 1,
          },
          {
            colId: 'lapsLed',
            hide: true,
            flex: 1,
          },
          {
            colId: 'lastLapTime',
            hide: true,
            flex: 1,
          },
          {
            colId: 'bestLapTime',
            flex: 1,
          },
          {
            colId: 'movingAverageLapTime',
            hide: true,
            flex: 1,
          },
          {
            colId: 'runName',
            hide: true,
            flex: 1,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
      },
      vs1: {
        id: 'vs1',
        showLeaderIndicator: true,
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
      },
      vs2: {
        id: 'vs2',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
      },
      vs3: {
        id: 'vs3',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
      },
      vs9: {
        id: 'vs9',
        columnStates: [
          {
            colId: 'cameraTakeTimestamp',
            flex: 1,
            sort: 'desc',
          },
          {
            colId: 'cameraLocation',
            flex: 1,
          },
          {
            colId: 'lowLineTemperature',
            flex: 1,
          },
          {
            colId: 'middleLineTemperature',
            flex: 1,
          },
          {
            colId: 'highLineTemperature',
            flex: 1,
          },
          {
            colId: 'referenceTemperature',
            flex: 1,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
      },
      vs4: {
        id: 'vs4',
      },
      vs5: {
        id: 'vs5',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        columnStates: [],
        dataFeedSelections: OriginType.MULTILOOP,
        localVehicleSelectionsSortOrder: [],
        sortOrder: ['1', '3', '5', '8', '9', '24', '48', '99'],
      },
      vs6: {
        id: 'vs6',
        focusedTab: 2,
      },
    },
    allIds: ['vs0', 'vs1', 'vs2', 'vs3', 'vs9', 'vs4', 'vs5', 'vs6'],
  },
  selectedCompetitorGroups: {
    1: 'vc1',
  },
  competitorGroups: {
    byId: {
      vc1: {
        id: 'vc1',
        name: '',
        seriesId: '1',
        vehicles: [
          'vh1',
          'vh2',
          'vh3',
          'vh4',
          'vh5',
          'vh6',
          'vh7',
          'vh8',
          'vh9',
          'vh10',
          'vh11',
          'vh12',
          'vh13',
          'vh14',
          'vh15',
          'vh16',
          'vh17',
          'vh18',
          'vh19',
          'vh20',
          'vh21',
          'vh22',
          'vh23',
          'vh24',
          'vh25',
          'vh26',
          'vh27',
          'vh28',
          'vh29',
          'vh30',
          'vh31',
          'vh32',
          'vh33',
          'vh34',
          'vh35',
          'vh36',
        ],
      },
    },
    allIds: ['vc1'],
  },
  competitorGroupVehicles: {
    byId: {
      vh1: {
        id: 'vh1',
        vehicleNumber: '1',
        colorId: '30',
        highlighted: true,
      },
      vh2: {
        id: 'vh2',
        vehicleNumber: '2',
        colorId: '37',
        highlighted: false,
      },
      vh3: {
        id: 'vh3',
        vehicleNumber: '3',
        colorId: '1',
        highlighted: true,
      },
      vh4: {
        id: 'vh4',
        vehicleNumber: '4',
        colorId: '37',
        highlighted: false,
      },
      vh5: {
        id: 'vh5',
        vehicleNumber: '5',
        colorId: '25',
        highlighted: true,
      },
      vh6: {
        id: 'vh6',
        vehicleNumber: '6',
        colorId: '37',
        highlighted: false,
      },
      vh7: {
        id: 'vh7',
        vehicleNumber: '7',
        colorId: '35',
        highlighted: false,
      },
      vh8: {
        id: 'vh8',
        vehicleNumber: '8',
        colorId: '24',
        highlighted: true,
      },
      vh9: {
        id: 'vh9',
        vehicleNumber: '9',
        colorId: '17',
        highlighted: true,
      },
      vh10: {
        id: 'vh10',
        vehicleNumber: '10',
        colorId: '37',
        highlighted: false,
      },
      vh11: {
        id: 'vh11',
        vehicleNumber: '11',
        colorId: '33',
        highlighted: false,
      },
      vh12: {
        id: 'vh12',
        vehicleNumber: '12',
        colorId: '37',
        highlighted: false,
      },
      vh13: {
        id: 'vh13',
        vehicleNumber: '20',
        colorId: '33',
        highlighted: false,
      },
      vh14: {
        id: 'vh14',
        vehicleNumber: '19',
        colorId: '33',
        highlighted: false,
      },
      vh15: {
        id: 'vh15',
        vehicleNumber: '17',
        colorId: '37',
        highlighted: false,
      },
      vh16: {
        id: 'vh16',
        vehicleNumber: '16',
        colorId: '35',
        highlighted: false,
      },
      vh17: {
        id: 'vh17',
        vehicleNumber: '15',
        colorId: '37',
        highlighted: false,
      },
      vh18: {
        id: 'vh18',
        vehicleNumber: '14',
        colorId: '37',
        highlighted: false,
      },
      vh19: {
        id: 'vh19',
        vehicleNumber: '21',
        colorId: '37',
        highlighted: false,
      },
      vh20: {
        id: 'vh20',
        vehicleNumber: '22',
        colorId: '37',
        highlighted: false,
      },
      vh21: {
        id: 'vh21',
        vehicleNumber: '23',
        colorId: '33',
        highlighted: false,
      },
      vh22: {
        id: 'vh22',
        vehicleNumber: '31',
        colorId: '35',
        highlighted: false,
      },
      vh23: {
        id: 'vh23',
        vehicleNumber: '24',
        colorId: '18',
        highlighted: true,
      },
      vh24: {
        id: 'vh24',
        vehicleNumber: '34',
        colorId: '37',
        highlighted: false,
      },
      vh25: {
        id: 'vh25',
        vehicleNumber: '38',
        colorId: '37',
        highlighted: false,
      },
      vh26: {
        id: 'vh26',
        vehicleNumber: '47',
        colorId: '35',
        highlighted: false,
      },
      vh27: {
        id: 'vh27',
        vehicleNumber: '45',
        colorId: '33',
        highlighted: false,
      },
      vh28: {
        id: 'vh28',
        vehicleNumber: '43',
        colorId: '35',
        highlighted: false,
      },
      vh29: {
        id: 'vh29',
        vehicleNumber: '42',
        colorId: '35',
        highlighted: false,
      },
      vh30: {
        id: 'vh30',
        vehicleNumber: '41',
        colorId: '37',
        highlighted: false,
      },
      vh31: {
        id: 'vh31',
        vehicleNumber: '48',
        colorId: '20',
        highlighted: true,
      },
      vh32: {
        id: 'vh32',
        vehicleNumber: '51',
        colorId: '37',
        highlighted: false,
      },
      vh33: {
        id: 'vh33',
        vehicleNumber: '54',
        colorId: '33',
        highlighted: false,
      },
      vh34: {
        id: 'vh34',
        vehicleNumber: '77',
        colorId: '35',
        highlighted: false,
      },
      vh35: {
        id: 'vh35',
        vehicleNumber: '78',
        colorId: '37',
        highlighted: false,
      },
      vh36: {
        id: 'vh36',
        vehicleNumber: '99',
        colorId: '14',
        highlighted: true,
      },
    },
    allIds: [
      'vh1',
      'vh2',
      'vh3',
      'vh4',
      'vh5',
      'vh6',
      'vh7',
      'vh8',
      'vh9',
      'vh10',
      'vh11',
      'vh12',
      'vh13',
      'vh14',
      'vh15',
      'vh16',
      'vh17',
      'vh18',
      'vh19',
      'vh20',
      'vh21',
      'vh22',
      'vh23',
      'vh24',
      'vh25',
      'vh26',
      'vh27',
      'vh28',
      'vh29',
      'vh30',
      'vh31',
      'vh32',
      'vh33',
      'vh34',
      'vh35',
      'vh36',
    ],
  },
  selectedReferenceVehicle: createEntity(),
  hideFooter: false,
  trackMapSettings: createEntity(),
};
